/**
 * evacuationQueueModel.js - 2026-10-20 (R2-29, docs/TODO_CURRENT.md; Dr. Tatar: capacity limits evacuation through the WAIT)
 *
 * Casualties above the day's evacuation capacity (ground and air, from the Tab 3 fleet by leg) cannot leave: they wait at the point of
 * injury or the casualty collection point, and the backlog carries to the next day. The wait that follows is ADDED to the wait before the
 * first movement derived from the operation (evacuationWindowModel.js); the wound curves price it in deaths. No separate death multiplier.
 *
 * Queue, hour by hour over a day (arrivals uniform over the day):
 *   urgent  = T1 casualties, evacuated first (NATO precedence: urgent before priority before routine) with the whole capacity;
 *   other   = everyone else, served with what the urgent leave.
 * The wait of an arrival is the queue ahead of it divided by the hourly capacity. Capacity 0 with casualties waiting = the maximum wait.
 *
 * Status of the numbers: the queue arithmetic is exact; MAX_QUEUE_WAIT_HOURS is the upper end of the prolonged casualty care window
 * (JTS PCC guidelines, 6 to 72 hours): EDUCATED GUESS as a cap. Capacity is shared equally by the brigades of the force (the fleet is
 * entered once for the force): EDUCATED GUESS, a demand-weighted share would be better.
 */
export const EVAC_QUEUE_STATUS = Object.freeze({
  status: 'EDUCATED_GUESS',
  basis: 'Queue arithmetic exact; wait capped at 72 h (upper end of the prolonged casualty care window); fleet shared equally by the brigades; urgent served first.'
});
export const MAX_QUEUE_WAIT_HOURS = 72;
export const URGENT_FIRST = true;

const waitOf = (queue, hourlyCapacity, anyDemand) => {
  if (!(queue > 0) && !(anyDemand && hourlyCapacity <= 0)) return 0;
  if (!(hourlyCapacity > 0)) return MAX_QUEUE_WAIT_HOURS;
  return Math.min(MAX_QUEUE_WAIT_HOURS, queue / hourlyCapacity);
};

/**
 * One day of the evacuation queue.
 * @param {Object} c backlogIn {urgent, other} (casualties), demand (casualties to evacuate today), urgentShare (T1 share 0..1),
 *                   capacity (casualties the fleet can move today, this brigade's share)
 * @returns {Object} urgent_wait_hours, other_wait_hours (mean over the day's arrivals), backlog_out {urgent, other}, moved, overflow
 */
export function evacuationQueueDay({ backlogIn, demand, urgentShare, capacity, hours = 24, urgentFirst = URGENT_FIRST }) {
  const num = (v, name) => { if (typeof v !== 'number' || Number.isNaN(v) || v < 0) throw new Error(`evacuationQueueModel: ${name} must be a number >= 0 (got: ${v})`); return v; };
  let U = num(backlogIn?.urgent, 'backlogIn.urgent');
  let R = num(backlogIn?.other, 'backlogIn.other');
  num(demand, 'demand'); num(capacity, 'capacity');
  if (typeof urgentShare !== 'number' || !(urgentShare >= 0 && urgentShare <= 1)) throw new Error(`evacuationQueueModel: urgentShare must be between 0 and 1 (got: ${urgentShare})`);
  const c = capacity / hours;
  const lu = (demand * urgentShare) / hours;
  const lr = (demand * (1 - urgentShare)) / hours;
  const anyDemand = demand > 0;
  let wU = 0, wR = 0, moved = 0;
  for (let h = 0; h < hours; h++) {
    if (urgentFirst) {
      wU += waitOf(U, c, lu > 0);
      wR += waitOf(U + R + Math.max(0, lu - c), c, lr > 0);   // behind the urgent queue and the urgent arrivals the hour's capacity cannot take
      const servedU = Math.min(U + lu, c);
      const servedR = Math.min(R + lr, Math.max(0, c - servedU));
      U = U + lu - servedU;
      R = R + lr - servedR;
      moved += servedU + servedR;
    } else {
      const w = waitOf(U + R, c, anyDemand);
      wU += w; wR += w;
      const served = Math.min(U + R + lu + lr, c);
      const total = U + R + lu + lr;
      const keep = total > 0 ? (total - served) / total : 0;
      U = (U + lu) * keep;
      R = (R + lr) * keep;
      moved += served;
    }
  }
  return {
    urgent_wait_hours: wU / hours,
    other_wait_hours: wR / hours,
    backlog_out: { urgent: U, other: R },
    moved,
    overflow: Math.max(0, demand - capacity)
  };
}
